import api from './api'
import { ApiResponse } from '../types/api'
import { FileUpload } from '../types/fileUpload'
import { FileType } from '../types/fileType'

export interface FileQueryParams {
  entityType?: string
  entityId?: number
  search?: string
  page?: number
  size?: number
}

type RawFileUpload = Omit<FileUpload, 'downloadUrl' | 'previewUrl' | 'viewUrl'> & {
  downloadUrl?: string
  previewUrl?: string
}

const normalizeBaseUrl = () => {
  const base = api.defaults.baseURL ?? ''
  return base.endsWith('/') ? base.slice(0, -1) : base
}

const mapFileUpload = (file: RawFileUpload): FileUpload => {
  const baseUrl = normalizeBaseUrl()
  const tagsArray =
    file.tagsArray && file.tagsArray.length > 0
      ? file.tagsArray
      : file.tags
        ? file.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : []

  const downloadUrl = `${baseUrl}/files/${file.id}/download`
  const viewUrl = `${baseUrl}/files/${file.id}/view`

  return {
    ...file,
    tagsArray,
    downloadUrl,
    previewUrl: file.previewUrl ?? viewUrl,
    viewUrl,
  }
}

const filterByParams = (files: FileUpload[], params?: FileQueryParams) => {
  if (!params) {
    return files
  }

  const searchTerm = params.search?.trim().toLowerCase()

  return files.filter((file) => {
    if (params.entityType && file.entityType?.toLowerCase() !== params.entityType.toLowerCase()) {
      return false
    }

    if (params.entityId && file.entityId !== params.entityId) {
      return false
    }

    if (searchTerm) {
      const haystack = [
        file.originalFilename,
        file.description,
        file.tags,
        file.tagsArray?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      if (!haystack.includes(searchTerm)) {
        return false
      }
    }

    return true
  })
}

export const fileService = {
  async getPdfs(params?: FileQueryParams): Promise<FileUpload[]> {
    const response = await api.get<ApiResponse<RawFileUpload[]>>('/files/extension/pdf', {
      params: {
        page: params?.page,
        size: params?.size,
      },
    })

    const files = response.data.data?.map(mapFileUpload) ?? []

    return filterByParams(
      files.filter((file) => file.fileType === FileType.DOCUMENT || file.fileExtension === 'pdf'),
      params
    ).sort((a, b) => {
      const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0
      const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0
      return dateB - dateA
    })
  },

  async searchDocuments(query: string, params?: FileQueryParams): Promise<FileUpload[]> {
    const response = await api.get<ApiResponse<RawFileUpload[]>>('/files/search', {
      params: { filename: query },
    })

    const files = response.data.data?.map(mapFileUpload) ?? []

    return filterByParams(files, params)
  },

  async getFilesByEntity(
    entityType: string,
    entityId: number,
    page: number = 0,
    size: number = 20
  ): Promise<FileUpload[]> {
    const response = await api.get<ApiResponse<RawFileUpload[]>>(
      `/files/entity/${entityType}/${entityId}`,
      {
        params: { page, size },
      }
    )

    return (response.data.data || []).map(mapFileUpload)
  },

  async uploadFile(
    file: File,
    entityType: string,
    entityId: number,
    uploadedBy: number,
    description?: string,
    tags?: string,
    isPublic?: boolean
  ): Promise<FileUpload> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('entityType', entityType)
    formData.append('entityId', entityId.toString())
    formData.append('uploadedBy', uploadedBy.toString())
    if (description) formData.append('description', description)
    if (tags) formData.append('tags', tags)
    if (isPublic !== undefined) formData.append('isPublic', isPublic.toString())

    const response = await api.post<ApiResponse<RawFileUpload>>('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return mapFileUpload(response.data.data)
  },
}





